// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import each from 'lodash/each';
import keys from 'lodash/keys';
import size from 'lodash/size';
import PropTypes from 'prop-types';
import { makeStyles, Button, Divider } from '@material-ui/core';

import MultiSelectMenu from '../MultiSelectMenu';
import { getI18n } from "../../../utils/intl";
import FormTextField from '../../FormTextField';
import styles from './styles';

const SHOW_ALL = 'SHOW_ALL';
const SHOW_SELECTED = 'SHOW_SELECTED';

const FilterByName = (props) => {
  const {
    hideCount, placeHolderText = {}, selectedItems = {},
    maxAllowedEntities, showSecondaryText, filterData, updateSelectedItems,
    handleSearchFilterNameChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    searchText: '',
    invalidSelection: false,
    visilbilityFilter: SHOW_ALL,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const onVisibilityFilterToggle = () => {
    const visilbilityFilter =
      state.visilbilityFilter === SHOW_ALL ? SHOW_SELECTED : SHOW_ALL;
    setState({
      visilbilityFilter,
    });
  };

  const getMenuItem = (val, key, showSecondaryText) => {
    if (showSecondaryText) {
      const { name, secondaryName, orgName, appName } = val;
      const secondaryTooltip = (<span><b>{appName}</b>({orgName})</span>);
      return ({ name, secondaryName, label: name, value: key, secondaryTooltip });
    }
    return ({ name: val, label: val, value: key });
  };

  const getMenuData = () => {
    const menuData = [];
    each(filterData, (val, key) => {
      const searchText = state.searchText.trim();
      if (state.visilbilityFilter === SHOW_SELECTED) {
        if (selectedItems[key]) {
          if (shouldIncludeItem(val, searchText, showSecondaryText)) {
            menuData.push(getMenuItem(val, key, showSecondaryText));
          }
        }
      } else if (shouldIncludeItem(val, searchText, showSecondaryText)) {
        menuData.push(getMenuItem(val, key, showSecondaryText));
      }
    });
    return menuData;
  };

  const shouldIncludeItem = (item, searchText, showSecondaryText) => {
    let name = '';
    let secondaryName = '';
    if (showSecondaryText) {
      name = item.name;
      secondaryName = item.secondaryName;
    } else {
      name = item;
    }
    const searchTextLC = searchText.toLowerCase();
    const nameLC = name && name.toLowerCase();
    const secondaryNameLC = secondaryName.toLowerCase();
    if ((searchText.length < 3) || (nameLC.includes(searchTextLC))
      || (secondaryName && secondaryNameLC.includes(searchTextLC))) {
      return true;
    }
    return false;
  };

  const handleSearch = value => {
    setState({
      searchText: value,
    });

    if ( handleSearchFilterNameChange ) {
      handleSearchFilterNameChange( value );
    }

  };

  const handleOnSelect = (value, checked) => {
    if (checked) {
      selectedItems[value] = true;
    } else if (selectedItems[value]) {
      delete selectedItems[value];
    }
    setState({ invalidSelection: size(selectedItems) > maxAllowedEntities });
    updateSelectedItems(selectedItems);
  };

  const selectedEntities = keys(selectedItems).length;
  const remainingSlots =
    maxAllowedEntities - selectedEntities < 0
      ? maxAllowedEntities
      : maxAllowedEntities - selectedEntities;
  const menuData = getMenuData();
  return (
    <div className={classes.filterContainer}>
      <div className={classes.searchBar}>
        <FormTextField
          fieldContainerClass={classes.searchFieldContainerClass}
          handleChange={handleSearch}
          id={'name-search'}
          placeholder={placeHolderText}
          value={state.searchText}
        />
      </div>
      <div className={classes.linkBar}>
        <Button
          className={classes.linkBtn}
          onClick={onVisibilityFilterToggle}
        >
          {state.visilbilityFilter === SHOW_ALL ? (
            intl.getI18nMessage(
              'label.analyticsDashboard.multiSelect.label.showSelected',
            )
          ) : intl.getI18nMessage(
              'label.analyticsDashboard.multiSelect.label.showAll',
          )}
        </Button>
      </div>
      <Divider className={classes.menuDivider} />
      <MultiSelectMenu
        data={menuData}
        selectedData={selectedItems}
        handleOnSelect={handleOnSelect}
        showSecondaryText={showSecondaryText}
      />
      {hideCount ? null : (
        <div className={classes.filterFooter}>
          <span className={classes.slotsDetail}>
            {remainingSlots}/{maxAllowedEntities} data slots remaining
          </span>
          {state.invalidSelection ? (
            <p className={classes.errorMessage}>
              {intl.getI18nMessage(
                'label.analytics.filter.filterByName.errorMessage.text',
              )}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

FilterByName.propTypes = {
  filterData: PropTypes.object,
  selectedItems: PropTypes.object,
  placeHolderText: PropTypes.string,
  updateSelectedItems: PropTypes.func,
  maxAllowedEntities: PropTypes.number,
  hideCount: PropTypes.bool,
  showSecondaryText: PropTypes.bool,
  handleSearchFilterNameChange: PropTypes.func,
};

export default FilterByName;